import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import {
  PageLoader,
  ProtectedRoute,
  PublicRoute,
  OnboardingRoute,
  QuestionnaireRoute,
  NotFoundPage,
  LanguageAwareRoute,
} from "@/components/core";
import { useMobileViewport } from "@/hooks/useMobileViewport";
import {
  buildOrgSubdomainAbsoluteUrl,
  getOrgFromSearchParams,
  isOrgSubdomainHostEnabled,
} from "@/features/auth/helpers";

// Lazy load route components for better performance
const LoginPage = lazy(
  () => import("@/features/auth/components/authentication/LoginPage")
);
const EntraCallbackPage = lazy(
  () => import("@/features/auth/components/authentication/EntraCallbackPage")
);
const LegacyOrgHostRedirect = lazy(
  () =>
    import(
      "@/features/auth/components/authentication/LegacyOrgHostRedirect"
    )
);
const SocialLinkingPage = lazy(
  () => import("@/features/auth/components/authentication/SocialLinkingPage")
);
const AccountCompletionPage = lazy(
  () =>
    import(
      "@/features/auth/components/authentication/AccountCompletionPage"
    )
);
const AccessDeniedScreen = lazy(
  () => import("@/features/auth/components/access/AccessDeniedScreen")
);
const AcceptInvitePage = lazy(
  () => import("@/features/auth/components/authentication/AcceptInvitePage")
);
const SignupPage = lazy(
  () => import("@/features/auth/components/authentication/SignupPage")
);
const PasswordResetScreen = lazy(
  () => import("@/features/auth/components/passwordManagement/PasswordResetScreen")
);
const ResetPasswordPage = lazy(
  () => import("@/features/auth/components/passwordManagement/ResetPasswordPage")
);
const UpdatePasswordScreen = lazy(
  () => import("@/features/auth/components/passwordManagement/UpdatePasswordScreen")
);
const SubscriptionScreen = lazy(
  () => import("@/features/auth/components/subscription/SubscriptionScreen")
);
const SubscriptionExpiredScreen = lazy(
  () => import("@/features/auth/components/subscription/SubscriptionExpiredScreen")
);
const PaymentSuccessScreen = lazy(
  () => import("@/features/auth/components/subscription/PaymentSuccessModal")
);
const TermsOfServicePage = lazy(
  () => import("@/features/auth/components/legal/TermsOfServicePage")
);
const PrivacyPolicyPage = lazy(
  () => import("@/features/auth/components/legal/PrivacyPolicyPage")
);
const VerificationSentScreen = lazy(
  () => import("@/features/auth/components/verification/VerificationSentScreen")
);
const VerificationSuccessScreen = lazy(
  () => import("@/features/auth/components/verification/VerificationSuccessScreen")
);
const FreemiumSuccessScreen = lazy(
  () => import("@/features/auth/components/subscription/FreemiumSuccessScreen")
);
const TrialExpiredScreen = lazy(
  () => import("@/features/auth/components/subscription/TrialExpiredScreen")
);
const PricingPage = lazy(
  () => import("@/features/pricing/components/PricingPage")
);
const IntroductionPage = lazy(() =>
  import("@/features/onboarding/components/IntroductionPage").then(
    (module) => ({ default: module.IntroductionPage })
  )
);
const IntroductionPageTwo = lazy(() =>
  import("@/features/onboarding/components/IntroductionPageTwo").then(
    (module) => ({ default: module.IntroductionPageTwo })
  )
);
const QuestionnaireInvitationPage = lazy(() =>
  import("@/features/onboarding/components/QuestionnaireInvitationPage").then(
    (module) => ({ default: module.QuestionnaireInvitationPage })
  )
);
const QuestionnaireStepperPage = lazy(() =>
  import("@/features/onboarding/components/QuestionnaireStepperPage").then(
    (module) => ({ default: module.QuestionnaireStepperPage })
  )
);
const AuthenticatedLayout = lazy(
  () => import("@/components/layout/AuthenticatedLayout")
);

function LegacyOrgQueryRedirect() {
  const location = useLocation();
  const org = getOrgFromSearchParams(location.search);
  const subdomainHostEnabled = isOrgSubdomainHostEnabled();
  const [subdomainRedirectFailed, setSubdomainRedirectFailed] = useState(false);

  useEffect(() => {
    if (!org || !subdomainHostEnabled || subdomainRedirectFailed) {
      return;
    }
    const remaining = new URLSearchParams(location.search);
    remaining.delete("org");
    const qs = remaining.toString();
    const path = qs ? `/login/?${qs}` : "/login/";
    try {
      window.location.replace(
        buildOrgSubdomainAbsoluteUrl(org, path, window.location)
      );
    } catch {
      setSubdomainRedirectFailed(true);
    }
  }, [org, subdomainHostEnabled, subdomainRedirectFailed, location.search]);

  if (org && subdomainHostEnabled && !subdomainRedirectFailed) {
    return <PageLoader />;
  }

  if (org && !subdomainHostEnabled) {
    const remaining = new URLSearchParams(location.search);
    remaining.delete("org");
    const qs = remaining.toString();
    return <Navigate to={`/login/${org}/${qs ? `?${qs}` : ""}`} replace />;
  }

  return (
    <PublicRoute>
      <LanguageAwareRoute>
        <LoginPage />
      </LanguageAwareRoute>
    </PublicRoute>
  );
}

function App() {
  // Initialize mobile viewport handling
  useMobileViewport();
  const subdomainHostEnabled = isOrgSubdomainHostEnabled();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes - Redirect to /chat if already authenticated */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LanguageAwareRoute>
                  <LoginPage />
                </LanguageAwareRoute>
              </PublicRoute>
            }
          />
          {/* Bare /login: legacy ?org= redirect or plain login */}
          <Route path="/login" element={<LegacyOrgQueryRedirect />} />
          {/* Host-scoped callback must be registered before /login/:orgKey */}
          <Route
            path="/login/entra/callback"
            element={
              <PublicRoute>
                <LanguageAwareRoute>
                  <EntraCallbackPage />
                </LanguageAwareRoute>
              </PublicRoute>
            }
          />
          {subdomainHostEnabled ? (
            <>
              <Route
                path="/login/:orgKey/entra/callback"
                element={<LegacyOrgHostRedirect />}
              />
              <Route
                path="/login/:orgKey"
                element={<LegacyOrgHostRedirect />}
              />
            </>
          ) : (
            <>
              <Route
                path="/login/:orgKey"
                element={
                  <PublicRoute>
                    <LanguageAwareRoute>
                      <LoginPage />
                    </LanguageAwareRoute>
                  </PublicRoute>
                }
              />
              <Route
                path="/login/:orgKey/entra/callback"
                element={
                  <PublicRoute>
                    <LanguageAwareRoute>
                      <EntraCallbackPage />
                    </LanguageAwareRoute>
                  </PublicRoute>
                }
              />
            </>
          )}
          <Route
            path="/auth/social/link"
            element={
              <PublicRoute>
                <LanguageAwareRoute>
                  <SocialLinkingPage />
                </LanguageAwareRoute>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/account/complete"
            element={
              <ProtectedRoute>
                <LanguageAwareRoute>
                  <AccountCompletionPage />
                </LanguageAwareRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accept-invite"
            element={
              <LanguageAwareRoute>
                <AcceptInvitePage />
              </LanguageAwareRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <LanguageAwareRoute>
                  <SignupPage />
                </LanguageAwareRoute>
              </PublicRoute>
            }
          />
          <Route
            path="/auth/password-reset"
            element={
              <PublicRoute>
                <PasswordResetScreen />
              </PublicRoute>
            }
          />
          {/** Removed confirmation route; success handled with toast */}
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          {/* Terms of Service - Accessible to everyone */}
          <Route
            path="/auth/terms-of-service"
            element={<TermsOfServicePage />}
          />
          {/* Privacy Policy - Accessible to everyone */}
          <Route path="/auth/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* Verification Routes - Protected (after authentication) */}
          <Route
            path="/auth/verification-sent"
            element={<VerificationSentScreen />}
          />
          <Route
            path="/auth/verification-success"
            element={<VerificationSuccessScreen />}
          />
          <Route
            path="/auth/freemium-success"
            element={<FreemiumSuccessScreen />}
          />
          <Route path="/trial-expired" element={<TrialExpiredScreen />} />

          {/* Protected Auth Routes - Require authentication */}
          <Route
            path="/auth/update-password"
            element={
              <ProtectedRoute>
                <UpdatePasswordScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/subscription-validation"
            element={
              <ProtectedRoute>
                <SubscriptionScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/subscription-expired"
            element={
              <ProtectedRoute>
                <SubscriptionExpiredScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/access-denied"
            element={
              <ProtectedRoute>
                <AccessDeniedScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <PaymentSuccessScreen />
              </ProtectedRoute>
            }
          />

          {/* Pricing - Accessible to authenticated users for plan selection */}
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />

          {/* Onboarding Routes - Require authentication and valid subscription plan */}
          <Route
            path="/onboarding/introduction"
            element={
              <ProtectedRoute>
                <IntroductionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/introduction-2"
            element={
              <ProtectedRoute>
                <IntroductionPageTwo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/questionnaire-invitation"
            element={
              <ProtectedRoute>
                <QuestionnaireRoute>
                  <OnboardingRoute>
                    <QuestionnaireInvitationPage />
                  </OnboardingRoute>
                </QuestionnaireRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/questionnaire"
            element={
              <ProtectedRoute>
                <QuestionnaireRoute>
                  <OnboardingRoute>
                    <QuestionnaireStepperPage />
                  </OnboardingRoute>
                </QuestionnaireRoute>
              </ProtectedRoute>
            }
          />

          {/* Protected App Routes - Require authenticated app access */}
          <Route
            path="/*"
            element={
              <ProtectedRoute requiresAppAccess={true}>
                <AuthenticatedLayout />
              </ProtectedRoute>
            }
          />

          {/* 404 Catch-all route - must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <Toaster />
    </div>
  );
}

export default App;
